import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import MultiSelectDropdown from "../../../Ui/UiJs/MultiSelectDropdown";

const uniqNumbers = (arr) =>
    Array.from(new Set((arr || []).map((x) => Number(x)).filter((x) => !Number.isNaN(x))));

const normalizePermission = (permission) => {
    const canConsult = Boolean(permission?.canConsult || permission?.canCreate || permission?.canValidate);
    return {
        userId: Number(permission?.userId),
        canConsult,
        canCreate: Boolean(permission?.canCreate),
        canValidate: Boolean(permission?.canValidate),
    };
};

function computePopulation(allUsers, filters, roleId) {
    const entiteIds = new Set(uniqNumbers(filters.entiteIds));
    const posteIds = new Set(uniqNumbers(filters.posteIds));
    const explicitUserIds = new Set(uniqNumbers(filters.userIds));
    const managerLevel = String(filters.managerLevelFilter || "").trim().toLowerCase();
    const filterMode = String(filters.filterMode || "IN").trim().toUpperCase().replace("_", "");

    const baseUsers = (allUsers || []).filter((user) => {
        if (!roleId) return true;
        return (user.roles || []).some((role) => Number(role.id) === Number(roleId));
    });

    const noFilters = !entiteIds.size && !posteIds.size && !explicitUserIds.size && !managerLevel;
    if (noFilters) {
        return baseUsers;
    }

    const nPlus1Ids = new Set(
        baseUsers
            .map((user) => Number(user.managerId))
            .filter((id) => !Number.isNaN(id) && id > 0)
    );

    const nPlus2Ids = new Set(
        baseUsers
            .filter((user) => nPlus1Ids.has(Number(user.id)))
            .map((user) => Number(user.managerId))
            .filter((id) => !Number.isNaN(id) && id > 0)
    );

    return baseUsers.filter((user) => {
        const entiteMatch = entiteIds.has(Number(user.entityId));
        const posteMatch = posteIds.has(Number(user.jobId));
        const explicitMatch = explicitUserIds.has(Number(user.id));
        const managerMatch =
            managerLevel === "n+1"
                ? nPlus1Ids.has(Number(user.id))
                : managerLevel === "n+2"
                    ? nPlus2Ids.has(Number(user.id))
                    : false;

        const match = entiteMatch || posteMatch || explicitMatch || managerMatch;
        return filterMode === "NOTIN" ? !match : match;
    });
}

function RepertoirePopulationPermissionsEditor({
                                                   allUsers = [],
                                                   allEntites = [],
                                                   allJobs = [],
                                                   roleId = "",
                                                   filters,
                                                   permissions,
                                                   onChangeFilters,
                                                   onChangePermissions,
                                               }) {
    const [search, setSearch] = useState("");
    const scopedUsers = useMemo(
        () =>
            (allUsers || []).filter((user) => {
                if (!roleId) return true;
                return (user.roles || []).some((role) => Number(role.id) === Number(roleId));
            }),
        [allUsers, roleId]
    );

    const population = useMemo(
        () => computePopulation(scopedUsers, filters, ""),
        [scopedUsers, filters]
    );

    useEffect(() => {
        const populationIds = new Set(population.map((user) => Number(user.id)));
        const currentMap = new Map((permissions || []).map((permission) => [Number(permission.userId), normalizePermission(permission)]));

        const nextPermissions = population
            .map((user) => currentMap.get(Number(user.id)) || {
                userId: Number(user.id),
                canConsult: false,
                canCreate: false,
                canValidate: false,
            })
            .map(normalizePermission);

        const currentNormalized = (permissions || []).map(normalizePermission).filter((permission) => populationIds.has(Number(permission.userId)));

        if (JSON.stringify(currentNormalized) !== JSON.stringify(nextPermissions)) {
            onChangePermissions(nextPermissions);
        }
    }, [population, permissions, onChangePermissions]);

    const filteredPopulation = useMemo(() => {
        const query = String(search || "").trim().toLowerCase();
        if (!query) return population;
        return population.filter((user) =>
            `${user.employeeCode || ""} ${user.firstName || ""} ${user.lastName || ""} ${user.email || ""}`
                .toLowerCase()
                .includes(query)
        );
    }, [population, search]);

    const permissionMap = useMemo(
        () => new Map((permissions || []).map((permission) => [Number(permission.userId), normalizePermission(permission)])),
        [permissions]
    );

    const updatePermission = (userId, patch) => {
        const next = (permissions || []).map((permission) => {
            if (Number(permission.userId) !== Number(userId)) return normalizePermission(permission);
            return normalizePermission({ ...permission, ...patch });
        });
        onChangePermissions(next);
    };

    return (
        <div className="rep-form-section">
            <h3 className="rep-form-title">Population cible</h3>

            <div className="rep-form-grid">
                <div className="rep-field">
                    <label>Filter mode</label>
                    <div className="rep-radio-group">
                        <label className="rep-pill-radio">
                            <input
                                type="radio"
                                name="filterMode"
                                value="IN"
                                checked={(filters.filterMode || "IN") === "IN"}
                                onChange={() => onChangeFilters({ ...filters, filterMode: "IN" })}
                            />
                            <span className={`rep-pill ${(filters.filterMode || "IN") === "IN" ? "active" : ""}`}>IN</span>
                        </label>
                        <label className="rep-pill-radio">
                            <input
                                type="radio"
                                name="filterMode"
                                value="NOTIN"
                                checked={(filters.filterMode || "IN") === "NOTIN"}
                                onChange={() => onChangeFilters({ ...filters, filterMode: "NOTIN" })}
                            />
                            <span className={`rep-pill ${(filters.filterMode || "IN") === "NOTIN" ? "active" : ""}`}>NOT IN</span>
                        </label>
                    </div>
                </div>

                <div className="rep-field">
                    <label>Niveau manager</label>
                    <div className="rep-radio-group">
                        <label className="rep-pill-radio">
                            <input
                                type="radio"
                                name="managerLevelFilter"
                                value=""
                                checked={!filters.managerLevelFilter}
                                onChange={() => onChangeFilters({ ...filters, managerLevelFilter: "" })}
                            />
                            <span className={`rep-pill ${!filters.managerLevelFilter ? "active" : ""}`}>Aucun</span>
                        </label>
                        <label className="rep-pill-radio">
                            <input
                                type="radio"
                                name="managerLevelFilter"
                                value="n+1"
                                checked={filters.managerLevelFilter === "n+1"}
                                onChange={() => onChangeFilters({ ...filters, managerLevelFilter: "n+1" })}
                            />
                            <span className={`rep-pill ${filters.managerLevelFilter === "n+1" ? "active" : ""}`}>oui</span>
                        </label>
                        <label className="rep-pill-radio">
                            <input
                                type="radio"
                                name="managerLevelFilter"
                                value="n+2"
                                checked={filters.managerLevelFilter === "n+2"}
                                onChange={() => onChangeFilters({ ...filters, managerLevelFilter: "n+2" })}
                            />
                            <span className={`rep-pill ${filters.managerLevelFilter === "n+2" ? "active" : ""}`}>non</span>
                        </label>
                    </div>
                </div>

                <div className="rep-field">
                    <MultiSelectDropdown
                        label="Entités"
                        items={allEntites || []}
                        value={uniqNumbers(filters.entiteIds)}
                        onChange={(value) => onChangeFilters({ ...filters, entiteIds: uniqNumbers(value) })}
                        placeholder="Sélectionner des entités..."
                        getId={(item) => Number(item.id)}
                        getLabel={(item) => item.nom || item.name || `Entité #${item.id}`}
                    />
                </div>

                <div className="rep-field">
                    <MultiSelectDropdown
                        label="Postes"
                        items={allJobs || []}
                        value={uniqNumbers(filters.posteIds)}
                        onChange={(value) => onChangeFilters({ ...filters, posteIds: uniqNumbers(value) })}
                        placeholder="Sélectionner des postes..."
                        getId={(item) => Number(item.id)}
                        getLabel={(item) => item.nom || item.name || `Poste #${item.id}`}
                    />
                </div>

                <div className="rep-field rep-field--full">
                    <MultiSelectDropdown
                        label="Utilisateurs"
                        items={scopedUsers || []}
                        value={uniqNumbers(filters.userIds)}
                        onChange={(value) => onChangeFilters({ ...filters, userIds: uniqNumbers(value) })}
                        placeholder="Sélectionner des utilisateurs..."
                        getId={(item) => Number(item.id)}
                        getLabel={(item) =>
                            item.fullName ||
                            `${item.firstName || ""} ${item.lastName || ""}`.trim() ||
                            `User #${item.id}`
                        }
                    />
                </div>
            </div>

            <div className="rep-hint" style={{ marginTop: 10 }}>
                Population calculée : {population.length} utilisateur(s)
            </div>

            {roleId ? (
                <div className="rep-hint" style={{ marginTop: 6 }}>
                    Les filtres et permissions sont limités aux utilisateurs rattachés au rôle du répertoire.
                </div>
            ) : (
                <div className="rep-hint" style={{ marginTop: 6 }}>
                    Sélectionnez d&apos;abord un rôle pour cadrer la population cible.
                </div>
            )}

            <h3 className="rep-form-title" style={{ marginTop: 18 }}>Permissions par utilisateur</h3>

            <div className="rep-users-search-wrap">
                <Search size={13} className="rep-users-search-icon" />
                <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Rechercher dans la population..."
                />
            </div>

            <div className="rep-permissions-table-wrap">
                <table className="rep-permissions-table">
                    <thead>
                    <tr>
                        <th>Utilisateur</th>
                        <th>Entité</th>
                        <th>Poste</th>
                        <th>Consulter</th>
                        <th>Créer</th>
                        <th>Valider</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredPopulation.length === 0 ? (
                        <tr>
                            <td colSpan={6} className="rep-empty-cell">Aucun utilisateur dans la population cible</td>
                        </tr>
                    ) : (
                        filteredPopulation.map((user) => {
                            const permission = permissionMap.get(Number(user.id)) || {
                                userId: Number(user.id),
                                canConsult: false,
                                canCreate: false,
                                canValidate: false,
                            };
                            console.log("useruseruser",user)
                            return (
                                <tr key={user.id}>
                                    <td>
                                        <div className="rep-user-info">
                                            <div className="rep-user-name">{user.fullName || `${user.firstName || ""} ${user.lastName || ""}`}</div>
                                            <div className="rep-user-sub">{user.employeeCode || "-"} • {user.email || "-"}</div>
                                        </div>
                                    </td>
                                    <td>{user.entityName || "-"}</td>
                                    <td>{user.jobName || "-"}</td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(permission.canConsult)}
                                            onChange={(event) => updatePermission(user.id, { canConsult: event.target.checked })}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(permission.canCreate)}
                                            onChange={(event) =>
                                                updatePermission(user.id, {
                                                    canCreate: event.target.checked,
                                                    canConsult: event.target.checked ? true : permission.canConsult,
                                                })
                                            }
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(permission.canValidate)}
                                            onChange={(event) =>
                                                updatePermission(user.id, {
                                                    canValidate: event.target.checked,
                                                    canConsult: event.target.checked ? true : permission.canConsult,
                                                })
                                            }
                                        />
                                    </td>
                                </tr>
                            );
                        })
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default RepertoirePopulationPermissionsEditor;

